import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ThreeDSecureAction } from '@recurly/react-recurly';
import Loading from '../../../../components/Loading';
import StripeElementsProvider, {
  HARDWARE_FIRST_APPEARANCE,
  HARDWARE_FIRST_GIFT_APPEARANCE,
} from '../../../../lib/StripeElementsProvider';
import AppPaths from '../../../../AppPaths';
import StripePaymentForm, { StripeAddressValue, StripeCheckoutSubmission } from '../../../../components/StripeCheckout';
import FiRecurlyProvider from '../../../../components/FiRecurlyProvider';
import InlineLoginForm from '../Confirm/InlineLoginForm';
import { loginQuiet } from '../../../../lib/authentication';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { CartPricingProvider, useCartPricing } from '../../../../contexts/CartPricingContext';
import { useDropAlreadyRedeemedGwaPromo } from '../../../../contexts/GwaPromoNoticeContext';
import { useLocale } from '../../../../contexts/LocaleContext';
import { isGwaPromoAlreadyRedeemed } from '../../../../lib/gwaPromo';
import { priceCartForCheckout, priceCartForPreview } from '../../../../lib/pricing';
import { useCartMode } from '../../../../lib/cartModes';
import { isExpeditedShippingAvailable } from '../../../../lib/shipping';
import { cartContainsOnlyFreeTrialBundles, getFreeTrialCartItemIds, giftCardCartItems } from '../../../../lib/cart';
import { giftableTrackers, hardwareFirstGiftCartItems } from '../../../../lib/hardwareFirstGiftCart';
import { isAmountGiftCardSku } from '../../../../lib/hardwareFirstGiftPlans';
import { formatHardwarePrice } from '../../../../lib/hardwareFirst';
import { getLocalizedPricing } from '../../../../lib/util/money';
import getCouponCookie from '../../../../lib/getCouponCookie';
import usePayPalInlineCheckout from '../../../../hooks/usePayPalInlineCheckout';
import useIsPayPalEnabled from '../../../../hooks/useIsPayPalEnabled';
import { cartActions } from '../../../../reducers/storeCart';
import OrderSummaryColumn from './OrderSummaryColumn';
import StripeCheckoutSubmitContext, {
  STRIPE_CHECKOUT_FORM_ID,
  StripeCheckoutSubmitState,
} from '../../../../components/StripeCheckout/StripeCheckoutSubmitContext';
import FreeTrialBlocker from './FreeTrialBlocker';
import * as types from '../../../../types';
import { isKlarnaBnplEnabled } from './klarnaBnpl';
import KlarnaBnplMessaging from './KlarnaBnplMessaging';
import useCheckoutV2 from '../../../../hooks/useCheckoutV2';
import useExpressWalletsTop from '../../../../hooks/useExpressWalletsTop';
import ExpressCheckoutRow from '../../../../components/ExpressCheckout';
import useHardwareFirstBnplOffered from '../../../../hooks/useHardwareFirstBnplOffered';
import useRescueCheckoutPrefill from '../../../../hooks/useRescueCheckoutPrefill';
import * as events from '../../../../lib/analytics/events';
import useStripeCheckout from './useStripeCheckout';
import { expressRowAmount } from './expressRowAmount';
import pageStyles from './StripeCheckoutPage.module.scss';

interface StripeCheckoutFormProps {
  stacked: boolean;
  onAddressUpdate: (address: StripeAddressValue) => void;
  onShippingCodeUpdate: (code: types.ShippingCode) => void;
}

/** a gifted tracker or an amount gift card; only these checkouts take the gifting design's look */
function useGiftCheckout(): boolean {
  const { cart } = useContext(CheckoutContext);
  const products = useSelector((state: types.AppState) => state.config.products);
  return (
    !!hardwareFirstGiftCartItems(cart, products) ||
    giftCardCartItems(cart).some((item) => isAmountGiftCardSku(item.lineItem.sku))
  );
}

function StripeCheckoutForm({ stacked, onAddressUpdate, onShippingCodeUpdate }: StripeCheckoutFormProps) {
  const {
    submitCheckout,
    submitting,
    error,
    resumingPurchase,
    threeDSecureActionToken,
    threeDSecurePending,
    onThreeDSecureToken,
    onThreeDSecureError,
    existingUserLogin,
    onExistingUserLogin,
    handleEmailComplete,
    handleEmailBlur,
    freeTrialBlocked,
    clearFreeTrialBlock,
  } = useStripeCheckout();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { cart, requiresShippingDetails } = useContext(CheckoutContext);
  const products = useSelector((state: types.AppState) => state.config.products);
  const { showPayPal } = useCartMode();
  const formatters = useLocale();
  const { currency } = formatters;
  const { totalInCents, hardwareFirstRecurringCharge } = useCartPricing();
  // the payment element flips to payment mode with this amount while a method charged inline at
  // purchase is selected: klarna when bnpl is offered, apple pay / google pay on hardware-first carts
  // on hardware-first carts. undefined = pricing not yet resolved.
  const bnplOffered = useHardwareFirstBnplOffered();
  const walletChargesInline = !!hardwareFirstRecurringCharge;
  const inlineChargeAmountInCents =
    totalInCents > 0 && ((isKlarnaBnplEnabled(currency) && bnplOffered) || walletChargesInline)
      ? totalInCents
      : undefined;
  const giftOrder = !!hardwareFirstGiftCartItems(cart, products);
  const amountGiftCards = giftCardCartItems(cart).filter((item) => isAmountGiftCardSku(item.lineItem.sku));
  const giftedTrackerIds = giftableTrackers(cart, products).map(({ cartItemId }) => cartItemId);
  // an amount card goes by email, so only a shipped line that is not a gift makes the order mixed
  const giftMixed =
    giftOrder &&
    Object.values(cart.cartItems).some(
      ({ cartItemId, lineItem }) => !giftedTrackerIds.includes(cartItemId) && !isAmountGiftCardSku(lineItem.sku),
    );
  const giftCardAmounts = Object.fromEntries(
    amountGiftCards.flatMap(({ cartItemId, lineItem }): [string, string][] => {
      const product = products.find((candidate) => 'sku' in candidate && candidate.sku === lineItem.sku);
      return product
        ? [[cartItemId, formatHardwarePrice(getLocalizedPricing(product.pricingInCents, currency), formatters)]]
        : [];
    }),
  );
  const giftCheckout = useGiftCheckout();
  const checkoutV2 = useCheckoutV2();
  const expressWalletsTop = useExpressWalletsTop();
  const expressAmountInCents = expressRowAmount({
    checkoutV2,
    expressWalletsTop,
    walletChargesInline,
    // the express sheet skips the form, so it cannot wait on the card fields either
    giftOrder: giftCheckout,
    inlineChargeAmountInCents,
  });
  // the purchase hook has one error slot; it renders under whichever surface submitted last
  const [lastSubmitSource, setLastSubmitSource] = useState<events.CheckoutSurface>('form');
  const { prefill, loading: prefillLoading } = useRescueCheckoutPrefill();

  useEffect(() => {
    events.shipping.viewed();
    events.payment.viewed();
  }, []);

  const [shippingCode, setShippingCode] = useState<types.ShippingCode | undefined>(undefined);
  const [address, setAddress] = useState<StripeAddressValue | undefined>(undefined);

  const isPayPalEnabled = useIsPayPalEnabled();
  const {
    handlePayPalToken,
    submitting: paypalSubmitting,
    error: paypalInlineError,
    existingUserLogin: paypalExistingUserLogin,
    onExistingUserLogin: onPayPalExistingUserLogin,
  } = usePayPalInlineCheckout();

  const handleAddressChange = useCallback(
    (addr: StripeAddressValue) => {
      setAddress(addr);
      onAddressUpdate?.(addr);
    },
    [onAddressUpdate],
  );

  const handleShippingCodeChange = useCallback(
    (code: types.ShippingCode) => {
      setShippingCode(code);
      onShippingCodeUpdate?.(code);
    },
    [onShippingCodeUpdate],
  );

  const canExpedite = address
    ? isExpeditedShippingAvailable({
        state: address.address.state,
        line1: address.address.line1,
        line2: address.address.line2,
      })
    : true;

  const handleSubmitCheckout = useCallback(
    (submission: StripeCheckoutSubmission) => {
      void submitCheckout({
        tokenId: submission.tokenId,
        email: submission.email,
        address: submission.address,
        shippingCode: submission.shippingCode,
        paymentMethodDetails: submission.paymentMethodDetails,
        isIdentityVerified: submission.isIdentityVerified,
        paymentType: submission.paymentType,
        chargedInline: submission.chargedInline,
        giftRecipient: submission.giftRecipient,
        billingAddress: submission.billingAddress,
        source: 'form',
      });
      setLastSubmitSource('form');
    },
    [submitCheckout],
  );

  const handleExpressCheckout = useCallback(
    (submission: StripeCheckoutSubmission) => {
      void submitCheckout({ ...submission, source: 'express' });
      setLastSubmitSource('express');
    },
    [submitCheckout],
  );

  const handleGiftCardDeliveryChange = useCallback(
    (id: string, giftCardDelivery: types.GiftCardDelivery) => {
      dispatch(cartActions.setGiftCardDelivery({ id, giftCardDelivery }));
    },
    [dispatch],
  );

  const handleRemoveFreeTrial = useCallback(() => {
    dispatch(cartActions.removeCartItems({ ids: getFreeTrialCartItemIds(cart, products) }));
    clearFreeTrialBlock();
    const couponCookie = getCouponCookie();
    if (couponCookie?.code) {
      dispatch(cartActions.addCoupon(couponCookie.code));
    }
  }, [cart, products, dispatch, clearFreeTrialBlock]);

  // back from the klarna redirect: the purchase completes in the background (ThreeDSecureAction
  // re-runs the pending action token), so show progress instead of a blank remounted form.
  if (resumingPurchase) {
    return (
      <div className={pageStyles.checkoutColumn}>
        <div className={pageStyles.resumeProcessing}>
          <Loading />
          <p>{t('completing_your_order')}</p>
        </div>
        {threeDSecureActionToken && (
          <FiRecurlyProvider>
            <ThreeDSecureAction
              actionTokenId={threeDSecureActionToken}
              onToken={(token) => void onThreeDSecureToken(token)}
              onError={onThreeDSecureError}
            />
          </FiRecurlyProvider>
        )}
      </div>
    );
  }

  // when an existing fi user is detected, show inline login instead of the checkout form
  if (existingUserLogin) {
    return (
      <div className={pageStyles.checkoutColumn}>
        <InlineLoginForm
          email={existingUserLogin.email}
          onLoginSuccess={(session) => void onExistingUserLogin(session)}
          loginFn={loginQuiet}
        />
      </div>
    );
  }

  if (paypalExistingUserLogin) {
    return (
      <div className={pageStyles.checkoutColumn}>
        <InlineLoginForm
          email={paypalExistingUserLogin.email}
          onLoginSuccess={(session) => void onPayPalExistingUserLogin(session)}
          loginFn={loginQuiet}
        />
      </div>
    );
  }

  // signed-in adopters only: stripe takes default values at mount, so the form waits for them
  if (prefillLoading) {
    return (
      <div className={pageStyles.checkoutColumn}>
        <Loading />
      </div>
    );
  }

  return (
    <div className={pageStyles.checkoutColumn}>
      {expressAmountInCents !== undefined && (
        <ExpressCheckoutRow
          amountInCents={expressAmountInCents}
          giftOrder={giftCheckout}
          onSubmitCheckout={handleExpressCheckout}
          disabled={submitting || threeDSecurePending || paypalSubmitting}
          error={lastSubmitSource === 'express' ? error : undefined}
        />
      )}
      <StripePaymentForm
        onSubmitCheckout={handleSubmitCheckout}
        onAddressChange={handleAddressChange}
        onShippingCodeChange={handleShippingCodeChange}
        onEmailComplete={handleEmailComplete}
        onEmailBlur={handleEmailBlur}
        inlineChargeAmountInCents={inlineChargeAmountInCents}
        walletChargesInline={walletChargesInline}
        anyPreorder={false}
        canExpedite={canExpedite}
        requiresShippingDetails={requiresShippingDetails}
        selectedShippingCode={shippingCode}
        submitting={submitting || threeDSecurePending || paypalSubmitting}
        serverError={(lastSubmitSource === 'form' ? error : undefined) || paypalInlineError}
        showPayPal={showPayPal && isPayPalEnabled}
        onPayPalComplete={(submission) => void handlePayPalToken(submission)}
        prefill={prefill}
        giftOrder={giftOrder}
        giftMixed={giftMixed}
        giftCards={amountGiftCards}
        giftCardAmounts={giftCardAmounts}
        onGiftCardDeliveryChange={handleGiftCardDeliveryChange}
        // embedded below the page's own content, focusing the email field would scroll past it on load
        autoFocusEmail={!stacked}
      />
      {freeTrialBlocked && (
        <FreeTrialBlocker
          onlyFreeTrials={cartContainsOnlyFreeTrialBundles(cart, products)}
          onRemoveFreeTrial={handleRemoveFreeTrial}
        />
      )}
      {threeDSecureActionToken && (
        <FiRecurlyProvider>
          <ThreeDSecureAction
            actionTokenId={threeDSecureActionToken}
            onToken={(token) => void onThreeDSecureToken(token)}
            onError={onThreeDSecureError}
          />
        </FiRecurlyProvider>
      )}
    </div>
  );
}

// stable identity so the elements options memo doesn't churn on every render
const EXCLUDE_KLARNA = ['klarna'];

function hardwareFirstAppearance(checkoutV2: boolean, giftCheckout: boolean) {
  if (!checkoutV2) {
    return undefined;
  }
  return giftCheckout ? HARDWARE_FIRST_GIFT_APPEARANCE : HARDWARE_FIRST_APPEARANCE;
}

/**
 * Stripe Elements for the checkout, always mounted in setup mode so card/ach/link keep the
 * store-then-charge flow (setup-mode tokens are the only ones recurly can save as billing info).
 * When klarna bnpl is offered, StripePaymentForm flips the element to payment mode while klarna
 * is the selected method (see its inlineChargeAmountInCents prop); when it isn't offered, klarna is
 * excluded outright so stripe's payment method configuration can't surface it into a flow that
 * can't complete a klarna order.
 */
function CheckoutStripeElements({
  currency,
  bnplEligible,
  checkoutV2 = false,
  giftCheckout = false,
  children,
}: {
  currency: types.FiCurrency;
  checkoutV2?: boolean;
  giftCheckout?: boolean;
  /**
   * klarna is offered only when bnpl is eligible: it needs the confirmation token the purchase sends.
   */
  bnplEligible: boolean;
  children: React.ReactNode;
}) {
  const klarnaOffered = bnplEligible && isKlarnaBnplEnabled(currency);
  return (
    <StripeElementsProvider
      mode="setup"
      currency={currency}
      excludedPaymentMethodTypes={klarnaOffered ? undefined : EXCLUDE_KLARNA}
      appearance={hardwareFirstAppearance(checkoutV2, giftCheckout)}
    >
      {children}
    </StripeElementsProvider>
  );
}

/**
 * Prices the cart, and when web turns the adoption promo down because the shopper's email already used
 * it, takes the promo off the cart and tells them. Returning nothing makes the provider price again once
 * the cart without the promo lands, instead of showing a figure that still counted it.
 */
function useCartPricingWithoutRedeemedGwaPromo() {
  const dropGwaPromo = useDropAlreadyRedeemedGwaPromo();

  return useCallback(
    async (cart: types.Cart, priceCart: () => Promise<types.CartPricing>) => {
      const pricing = await priceCart();
      if (isGwaPromoAlreadyRedeemed(pricing.validationErrors?.couponCode)) {
        dropGwaPromo(cart.couponCode, 'checkout-pricing');
        return undefined;
      }

      return pricing;
    },
    [dropGwaPromo],
  );
}

function InlineWalletsContent({
  currency,
  stacked,
  onPriced,
}: {
  currency: types.FiCurrency;
  stacked: boolean;
  onPriced?: (pricing: types.CartPricing) => void;
}) {
  const { cart } = useContext(CheckoutContext);
  const allShippingOptions = useSelector((state: types.AppState) => state.config.shippingOptions);
  const addressRef = useRef<StripeAddressValue | undefined>(undefined);
  const shippingCodeRef = useRef<types.ShippingCode | undefined>(undefined);
  const [selectedShippingOption, setSelectedShippingOption] = useState<types.IShippingOption | undefined>(undefined);
  const [pricingVersion, setPricingVersion] = useState(0);
  const priceWithoutRedeemedGwaPromo = useCartPricingWithoutRedeemedGwaPromo();

  const fetchPricing = useCallback(
    () =>
      priceWithoutRedeemedGwaPromo(cart, () => {
        const addr = addressRef.current;
        const code = shippingCodeRef.current;
        if (pricingVersion > 0 && addr && code && addr.address.postal_code) {
          return priceCartForCheckout(cart, {
            shippingCode: code,
            address: {
              country: addr.address.country,
              postalCode: addr.address.postal_code,
            },
          });
        }
        return priceCartForPreview(cart);
      }),
    [cart, pricingVersion, priceWithoutRedeemedGwaPromo],
  );

  const lastPricingAddressRef = useRef<{ postalCode: string; country: string } | null>(null);

  const handleAddressUpdate = useCallback((addr: StripeAddressValue) => {
    addressRef.current = addr;
    const { postal_code: postalCode, country } = addr.address;
    // only re-price when the fields that affect tax/shipping actually change
    if (
      shippingCodeRef.current &&
      postalCode &&
      (lastPricingAddressRef.current?.postalCode !== postalCode || lastPricingAddressRef.current?.country !== country)
    ) {
      lastPricingAddressRef.current = { postalCode, country };
      setPricingVersion((v) => v + 1);
    }
  }, []);

  const handleShippingCodeUpdate = useCallback(
    (code: types.ShippingCode) => {
      shippingCodeRef.current = code;
      setSelectedShippingOption(allShippingOptions.find((so) => so.code === code));
      // only bump pricing if we have a valid address
      if (addressRef.current?.address.postal_code) {
        setPricingVersion((v) => v + 1);
      }
    },
    [allShippingOptions],
  );

  return (
    <CartPricingProvider fetchPricing={fetchPricing}>
      <InlineWalletsElements
        currency={currency}
        stacked={stacked}
        onPriced={onPriced}
        onAddressUpdate={handleAddressUpdate}
        onShippingCodeUpdate={handleShippingCodeUpdate}
        selectedShippingOption={selectedShippingOption}
      />
    </CartPricingProvider>
  );
}

/** the klarna gate reads cart pricing, so this sits under the provider InlineWalletsContent renders */
/**
 * Calls `onPriced` once, with the first pricing the checkout renders. Strict Mode re-runs the effect
 * but the ref survives it, so the call happens once per mount.
 */
function useReportPricedOnce(onPriced?: (pricing: types.CartPricing) => void) {
  const pricing = useCartPricing();
  const reported = useRef(false);

  useEffect(() => {
    if (onPriced && !reported.current) {
      reported.current = true;
      onPriced(pricing);
    }
  }, [onPriced, pricing]);
}

function InlineWalletsElements({
  currency,
  stacked,
  onPriced,
  onAddressUpdate,
  onShippingCodeUpdate,
  selectedShippingOption,
}: {
  currency: types.FiCurrency;
  stacked: boolean;
  onPriced?: (pricing: types.CartPricing) => void;
  onAddressUpdate: (address: StripeAddressValue) => void;
  onShippingCodeUpdate: (code: types.ShippingCode) => void;
  selectedShippingOption: types.IShippingOption | undefined;
}) {
  useReportPricedOnce(onPriced);
  const { t } = useTranslation();
  const bnplOffered = useHardwareFirstBnplOffered();
  const checkoutV2 = useCheckoutV2();
  const giftCheckout = useGiftCheckout();
  // v2 desktop: the form reports its submit button here and the summary column draws it
  const [submitState, setSubmitState] = useState<StripeCheckoutSubmitState | undefined>(undefined);

  return (
    <CheckoutStripeElements
      currency={currency}
      bnplEligible={bnplOffered}
      checkoutV2={checkoutV2}
      giftCheckout={giftCheckout}
    >
      <div className={classNames({ [pageStyles.v2Page]: checkoutV2 && !stacked })}>
        {checkoutV2 && !stacked && (
          <div className={pageStyles.backRow}>
            <Link className={pageStyles.backLink} to={AppPaths.Bag}>
              {t('hardware_first_checkout_back_to_cart')}
            </Link>
          </div>
        )}
        <StripeCheckoutSubmitContext.Provider value={checkoutV2 && !stacked ? setSubmitState : undefined}>
          <div
            className={classNames(pageStyles.splitLayout, {
              [pageStyles.v2]: checkoutV2,
              [pageStyles.stacked]: stacked,
            })}
          >
            <div className={`${pageStyles.orderSummary} ${pageStyles.orderSummaryMobileFirst}`}>
              <div className={classNames(pageStyles.orderSummaryInner, { [pageStyles.summaryCardV2]: checkoutV2 })}>
                <OrderSummaryColumn
                  selectedShippingOption={selectedShippingOption}
                  checkoutFlow="stripe"
                  // embedded: the summary is the price the page states once, so it opens itemized
                  defaultExpanded={stacked}
                  hideEditCart={stacked}
                />
              </div>
              {checkoutV2 && !stacked && submitState && !submitState.hidden && (
                <button
                  className={pageStyles.asidePlaceOrder}
                  disabled={submitState.disabled}
                  form={STRIPE_CHECKOUT_FORM_ID}
                  type="submit"
                >
                  {submitState.loading ? <Loading className={pageStyles.asideLoading} /> : submitState.label}
                </button>
              )}
            </div>
            <div className={pageStyles.paymentColumn}>
              {bnplOffered && <KlarnaBnplMessaging />}
              <StripeCheckoutForm
                stacked={stacked}
                onAddressUpdate={onAddressUpdate}
                onShippingCodeUpdate={onShippingCodeUpdate}
              />
            </div>
          </div>
        </StripeCheckoutSubmitContext.Provider>
      </div>
    </CheckoutStripeElements>
  );
}

export default function StripeCheckoutPage() {
  const { currency } = useLocale();

  return <InlineWalletsContent currency={currency} stacked={false} />;
}

/**
 * The same checkout for a page that embeds it in a column of its own (the rescue one-page checkout)
 * rather than giving it the window: summary over form at every width, and no back-to-cart link,
 * since there is no cart behind it. `onPriced` fires once with the first pricing shown, which is where
 * such a page reports the checkout as started (the cart's Checkout button does it on the regular path).
 */
export function StackedStripeCheckout({ onPriced }: { onPriced?: (pricing: types.CartPricing) => void }) {
  const { currency } = useLocale();

  return <InlineWalletsContent currency={currency} stacked onPriced={onPriced} />;
}
